import { useEffect, useCallback } from "react";

// Custom hook for handling browser close/refresh events during active sessions
// Shows confirmation dialog and sends session data via keepalive fetch

export const useWindowCloseHandler = (
  sessionId,
  userId,
  apiBaseURL,
  isActive = true,
  sessionState = "active"
) => {
  const handleBeforeUnload = useCallback(
    (event) => {
      if (!isActive || !sessionId) return;

      let message;
      switch (sessionState) {
        case "popup":
          message =
            "You have an active popup. Are you sure you want to leave? Your progress may be lost.";
          break;
        case "ending":
          message =
            "Your session is ending. Are you sure you want to leave now?";
          break;
        case "active":
        default:
          message =
            "Your coaching session is still active. Are you sure you want to leave?";
      }

      event.preventDefault();
      event.returnValue = message;
      return message;
    },
    [isActive, sessionId, sessionState]
  );

  const fallbackRequest = useCallback((url, data) => {
    try {
      if (window.fetch) {
        window.fetch(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: data,
          keepalive: true,
        }).catch(() => {});
      } else {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url, false);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.send(data);
      }
    } catch (error) {
      console.warn("Failed to send session end data:", error);
    }
  }, []);

  const handleUnload = useCallback(() => {
    if (!isActive || !sessionId) return;

    const url = `${apiBaseURL}/users/session/${sessionId}/end`;
    const data = JSON.stringify({
      closed_at: new Date().toISOString(),
      reason: "window_close",
      user_id: userId,
      session_state: sessionState,
    });

    // Use keepalive fetch so auth headers can be attached by our fetch wrapper.
    fallbackRequest(url, data);
  }, [sessionId, apiBaseURL, userId, isActive, sessionState, fallbackRequest]);

  useEffect(() => {
    if (!isActive) return;

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);
    window.addEventListener("pagehide", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
      window.removeEventListener("pagehide", handleUnload);
    };
  }, [handleBeforeUnload, handleUnload, isActive]);
};

export const useSimpleWindowCloseHandler = (
  sessionId,
  userId,
  apiBaseURL,
  isActive = true
) => {
  return useWindowCloseHandler(
    sessionId,
    userId,
    apiBaseURL,
    isActive,
    "active"
  );
};

export const usePopupWindowCloseHandler = (
  sessionId,
  userId,
  apiBaseURL,
  isActive = true
) => {
  return useWindowCloseHandler(
    sessionId,
    userId,
    apiBaseURL,
    isActive,
    "popup"
  );
};

export default useWindowCloseHandler;
